import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import type { SourceRef } from './primitives'

/**
 * "Lots" reconciliation, built entirely on existing endpoints (no new backend):
 *   - GET /assets?ancestor_uid&asset_type_code=unit|floor&include=flags,properties,frontend_data
 *   - POST /assets/approve            (confirm extracted / to_approve units)
 *   - PATCH /assets/{uid}             (clear the frontend_data.dr_pending marker — Option A)
 *   - POST /assets/{uid}/properties   (apply a layer value in bulk)
 *
 * A unit is PENDING when it carries the `to_approve` flag (extraction) OR a
 * `frontend_data.dr_pending` marker (socle / manual). Both resolve here.
 */

export type LotType = 'resid' | 'commerce' | 'loge' | 'parking' | 'cave' | string

export type LotException = { scope: 'structure' | string; reason: string }

export type Lot = {
  uid: string
  num: string
  floorUid: string
  type: LotType
  surface: number | null
  layers: Record<string, string | null>
  pending: boolean
  /** pending via the to_approve flag (vs a frontend_data marker) */
  toApprove: boolean
  frontendData: Record<string, unknown>
  /**
   * Always resolvable (spec §1.2: "no value renders without a resolvable
   * source. No exceptions."). In practice a lot always comes from
   * extraction and carries dr_source; the manual-add fallback below guards
   * a future manual-creation path or malformed data, so the guarantee holds
   * unconditionally rather than by convention.
   */
  source: SourceRef
}

export type LotsFloor = { uid: string; label: string; order: number }

/** Layer config — adding a layer is a config entry, no per-layer UI (spec §7.2). */
export type LayerConfig = {
  id: string
  code: string
  labelKey: string
  common: string
  show: (v: string | null) => string
}

export const LAYERS: LayerConfig[] = [
  { id: 'heat', code: 'heating_type', labelKey: 'lots.field_heat', common: 'gas', show: (v) => (v === 'gas' ? 'gaz' : v === 'elec' ? 'élec' : v ? v : '?') },
  { id: 'ecs', code: 'hot_water_type', labelKey: 'lots.field_ecs', common: 'gas', show: (v) => (v === 'gas' ? 'gaz' : v === 'elec' ? 'cumulus' : v ? v : '?') },
  { id: 'volet', code: 'shutter_type', labelKey: 'lots.field_volet', common: 'roulant', show: (v) => (v === 'roulant' ? 'roulant' : v === 'battant' ? 'battant' : v === 'na' ? '—' : v ? v : '?') },
]

const decode = (v: unknown): unknown => {
  if (typeof v !== 'string') return v
  try {
    return JSON.parse(atob(v))
  } catch {
    return v
  }
}

function floorOrder(label: string): number {
  const l = label.toLowerCase()
  if (l.includes('annexe')) return -100
  if (l.includes('sous-sol') || l.includes('ss')) return -10
  if (l === 'rdc' || l.includes('rez')) return 0
  const m = label.match(/(\d+)/)
  return m ? parseInt(m[1], 10) : 1
}

type RawAsset = {
  uid: string
  name?: string
  parent_asset_uid?: string
  flags?: string[]
  frontend_data?: Record<string, unknown>
  properties?: Array<{ code: string; value: unknown }>
}

function listAssets(org: string, buildingUid: string, typeCode: string, includes: string[]) {
  return apiClient
    .GET('/api/v1/organizations/{orgCode}/assets', {
      params: {
        path: { orgCode: org },
        query: { ancestor_uid: buildingUid, asset_type_code: typeCode, include: includes, limit: 500 } as never,
      },
    })
    .then((r) => ((r.data as { data?: RawAsset[] })?.data ?? []))
}

export function useLots(org: string, buildingUid: string) {
  const queryClient = useQueryClient()
  const unitsKey = ['dr-units', org, buildingUid] as const
  const floorsKey = ['dr-floors', org, buildingUid] as const

  const floorsQ = useQuery({
    queryKey: floorsKey,
    queryFn: () => listAssets(org, buildingUid, 'floor', ['flags']),
    enabled: !!org && !!buildingUid,
  })
  const unitsQ = useQuery({
    queryKey: unitsKey,
    queryFn: () => listAssets(org, buildingUid, 'unit', ['flags', 'properties', 'frontend_data']),
    enabled: !!org && !!buildingUid,
  })

  const floors: LotsFloor[] = useMemo(() => {
    const fs = (floorsQ.data ?? []).map((f) => ({ uid: f.uid, label: f.name || '—', order: floorOrder(f.name || '') }))
    return fs.sort((a, b) => b.order - a.order)
  }, [floorsQ.data])

  const lots: Lot[] = useMemo(() => {
    return (unitsQ.data ?? []).map((u) => {
      const props: Record<string, unknown> = {}
      for (const p of u.properties ?? []) props[p.code] = decode(p.value)
      const fd = u.frontend_data ?? {}
      const toApprove = (u.flags ?? []).includes('to_approve')
      const drPending = fd.dr_pending === true
      const src = fd.dr_source as { document_filename?: string; document_uid?: string; page?: number | string } | undefined
      const layers: Record<string, string | null> = {}
      for (const layer of LAYERS) {
        const v = props[layer.code]
        layers[layer.code] = v == null ? null : String(v)
      }
      const surfaceRaw = props.surface_area
      return {
        uid: u.uid,
        num: props.unit_nb != null ? String(props.unit_nb) : (u.name || '').replace(/^Lot\s+/i, ''),
        floorUid: u.parent_asset_uid || '',
        type: (props.unit_type != null ? String(props.unit_type) : 'resid') as LotType,
        surface: typeof surfaceRaw === 'number' ? surfaceRaw : surfaceRaw ? Number(surfaceRaw) || null : null,
        layers,
        pending: toApprove || drPending,
        toApprove,
        frontendData: fd,
        source: src
          ? { documentUid: src.document_uid, documentName: src.document_filename, page: src.page }
          : { manualLabel: 'Saisie' },
      }
    })
  }, [unitsQ.data])

  /** Compute exceptions for a lot in the given mode (structure or a layer id). */
  const exceptionsFor = useCallback(
    (lot: Lot, mode: string): LotException[] => {
      const out: LotException[] = []
      if (mode === 'structure') {
        if (lot.type === 'resid' && lot.surface == null) out.push({ scope: 'structure', reason: 'Surface manquante' })
        // numbering gap within the lot's floor (resid only)
        const sameFloor = lots.filter((l) => l.floorUid === lot.floorUid && l.type === 'resid')
        const nums = sameFloor.map((l) => parseInt(l.num.replace(/\D/g, ''), 10)).filter((n) => !isNaN(n)).sort((a, b) => a - b)
        if (nums.length > 1) {
          const mine = parseInt(lot.num.replace(/\D/g, ''), 10)
          const max = nums[nums.length - 1]
          const min = nums[0]
          for (let n = min; n <= max; n++) {
            if (!nums.includes(n) && Math.abs(n - mine) === 1) {
              out.push({ scope: 'structure', reason: `Trou de numérotation (manque ${n})` })
              break
            }
          }
        }
      } else {
        const layer = LAYERS.find((l) => l.id === mode)
        if (layer && lot.type === 'resid') {
          const v = lot.layers[layer.code]
          if (v && v !== layer.common) out.push({ scope: layer.id, reason: `Valeur différente du motif (${layer.show(v)})` })
        }
      }
      return out
    },
    [lots],
  )

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dr-units', org, buildingUid] })
    queryClient.invalidateQueries({ queryKey: ['dr-floors', org, buildingUid] })
  }, [queryClient, org, buildingUid])

  /** Confirm a set of lots — routes to /assets/approve or clears the marker. */
  const confirmLots = useCallback(
    async (uids: string[]) => {
      const targets = lots.filter((l) => uids.includes(l.uid) && l.pending)
      const toApprove = targets.filter((l) => l.toApprove).map((l) => l.uid)
      const markers = targets.filter((l) => !l.toApprove)
      if (toApprove.length) {
        await apiClient.POST('/api/v1/organizations/{orgCode}/assets/approve', {
          params: { path: { orgCode: org } },
          body: { uids: toApprove, action: 'approve' } as never,
        })
      }
      for (const m of markers) {
        await apiClient.PATCH('/api/v1/organizations/{orgCode}/assets/{assetUid}', {
          params: { path: { orgCode: org, assetUid: m.uid } },
          body: { frontend_data: { ...m.frontendData, dr_pending: false } } as never,
        })
      }
      invalidate()
    },
    [lots, org, invalidate],
  )

  /** Apply a layer's common value to every residential, non-exception lot. */
  const applyLayer = useCallback(
    async (layerId: string) => {
      const layer = LAYERS.find((l) => l.id === layerId)
      if (!layer) return
      const targets = lots.filter((l) => l.type === 'resid' && exceptionsFor(l, layerId).length === 0)
      for (const l of targets) {
        await apiClient.POST('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties', {
          params: { path: { orgCode: org, assetUid: l.uid } },
          body: { properties: [{ property_code: layer.code, value: layer.common, source_details: { source: 'manual' } }] } as never,
        })
      }
      invalidate()
    },
    [lots, org, exceptionsFor, invalidate],
  )

  return {
    floors,
    lots,
    isLoading: unitsQ.isLoading || floorsQ.isLoading,
    exceptionsFor,
    confirmLots,
    applyLayer,
  }
}
